import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE } from '../App';

// ── Pure helpers (module-level — never recreated) ─────────────────────────────

function splitName(fullName) {
  const parts = (fullName || '').trim().split(/\s+/);
  if (parts.length === 1) return { first: parts[0] || '', last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

const NAME_RE    = /^[a-zA-Z\s]+$/;
const EMAIL_RE   = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SPECIAL_RE = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

// ── Static styles (module-level — never recreated) ────────────────────────────

const labelStyle = {
  fontSize: '13px', color: '#22272D', fontWeight: '500', display: 'block',
};

const errorStyle = {
  fontSize: '11px', color: '#FF766B', marginTop: '4px',
};

function inputStyle(hasError) {
  return {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: `0.5px solid ${hasError ? '#FF766B' : '#AAB0B8'}`,
    background: '#F4FAFF', color: '#22272D', fontSize: '13px',
    fontFamily: 'Manrope, sans-serif', outline: 'none',
    marginTop: '6px', boxSizing: 'border-box',
  };
}

function msgBoxStyle(type) {
  return {
    padding: '10px 14px', borderRadius: '8px', marginBottom: '16px',
    background: type === 'success' ? 'rgba(189,255,192,0.3)' : 'rgba(255,118,107,0.2)',
    color:      type === 'success' ? '#1a6b1a'               : '#a83232',
    fontSize: '13px',
  };
}

const saveBtn = (disabled) => ({
  background: '#F1FF5C', color: '#22272D', border: 'none',
  padding: '10px 24px', borderRadius: '8px', fontSize: '14px',
  fontWeight: '600', fontFamily: 'Manrope, sans-serif',
  cursor:     disabled ? 'not-allowed' : 'pointer',
  opacity:    disabled ? 0.7 : 1,
  transition: 'opacity 0.2s ease',
});

const pwWrapStyle = {
  position: 'relative', display: 'flex', alignItems: 'center',
};

const showHideBtnStyle = {
  position: 'absolute', right: '10px', top: '50%',
  transform: 'translateY(-50%)', background: 'none',
  border: 'none', cursor: 'pointer', fontSize: '12px',
  color: '#AAB0B8', padding: '2px 4px', marginTop: '3px',
};

// ── ShowHideBtn sub-component ─────────────────────────────────────────────────

function ShowHideBtn({ show, onToggle }) {
  return (
    <button type="button" style={showHideBtnStyle} onClick={onToggle} tabIndex={-1}>
      {show ? 'Hide' : 'Show'}
    </button>
  );
}

// ── useFlashMsg hook — auto-clears message, cleans up on unmount ──────────────

function useFlashMsg() {
  const [msg,     setMsg]     = useState('');
  const [msgType, setMsgType] = useState('');
  const timerRef = useRef(null);

  const flash = useCallback((message, type) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setMsg(message);
    setMsgType(type);
    timerRef.current = setTimeout(() => { setMsg(''); setMsgType(''); }, 3000);
  }, []);

  // Clear timer on unmount
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return { msg, msgType, flash };
}

// ── Main component ────────────────────────────────────────────────────────────

function Settings({ user, onUserUpdate }) {
  // Resync local name state whenever the user prop changes (e.g. after save)
  const { first: initialFirst, last: initialLast } = splitName(user.name);

  const [firstName,   setFirstName]   = useState(initialFirst);
  const [lastName,    setLastName]    = useState(initialLast);
  const [email,       setEmail]       = useState(user.email || '');
  const [nameErrors,  setNameErrors]  = useState({});
  const [emailError,  setEmailError]  = useState('');
  const [pwErrors,    setPwErrors]    = useState({});
  const [nameSaving,  setNameSaving]  = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [pwSaving,    setPwSaving]    = useState(false);

  const [currentPw,  setCurrentPw]  = useState('');
  const [newPw,      setNewPw]      = useState('');
  const [confirmPw,  setConfirmPw]  = useState('');

  // Show/hide toggles for each password field
  const [showCurrent,  setShowCurrent]  = useState(false);
  const [showNew,      setShowNew]      = useState(false);
  const [showConfirm,  setShowConfirm]  = useState(false);

  const nameFlash  = useFlashMsg();
  const emailFlash = useFlashMsg();
  const pwFlash    = useFlashMsg();

  // Resync inputs when parent user object changes after a successful save
  useEffect(() => {
    const { first, last } = splitName(user.name);
    setFirstName(first);
    setLastName(last);
    setEmail(user.email || '');
  }, [user]);

  // ── Name save ───────────────────────────────────────────────────────────────

  const handleNameSave = useCallback(async () => {
    const errors = {};
    if (!firstName.trim())                        errors.firstName = 'Required';
    else if (!NAME_RE.test(firstName.trim()))     errors.firstName = 'Letters only';
    if (lastName && !NAME_RE.test(lastName.trim())) errors.lastName = 'Letters only';
    if (Object.keys(errors).length) { setNameErrors(errors); return; }
    setNameErrors({});
    setNameSaving(true);
    try {
      const fullName = lastName.trim()
        ? `${firstName.trim()} ${lastName.trim()}`
        : firstName.trim();
      await axios.post(
        `${API_BASE}/api/users/edit`,
        { user_id: user.id, name: fullName, email: user.email, role: user.role, password: null },
        { withCredentials: true },
      );
      onUserUpdate({ ...user, name: fullName });
      nameFlash.flash('✓ Name updated successfully!', 'success');
    } catch (err) {
      nameFlash.flash(err.response?.data?.message || 'Failed to update name', 'error');
    } finally {
      setNameSaving(false);
    }
  }, [firstName, lastName, user, onUserUpdate, nameFlash]);

  // ── Email save ──────────────────────────────────────────────────────────────

  const handleEmailSave = useCallback(async () => {
    if (!email.trim())              { setEmailError('Required');      return; }
    if (!EMAIL_RE.test(email))      { setEmailError('Invalid email'); return; }
    setEmailError('');
    setEmailSaving(true);
    try {
      const trimmed = email.trim().toLowerCase();
      await axios.post(
        `${API_BASE}/api/users/edit`,
        { user_id: user.id, name: user.name, email: trimmed, role: user.role, password: null },
        { withCredentials: true },
      );
      onUserUpdate({ ...user, email: trimmed });
      emailFlash.flash('✓ Email updated successfully!', 'success');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update email';
      if (msg.toLowerCase().includes('email') || msg.toLowerCase().includes('use')) {
        setEmailError(msg);
      } else {
        emailFlash.flash(msg, 'error');
      }
    } finally {
      setEmailSaving(false);
    }
  }, [email, user, onUserUpdate, emailFlash]);

  // ── Password save ───────────────────────────────────────────────────────────

  const handlePasswordSave = useCallback(async () => {
    const errors = {};
    if (!currentPw) errors.currentPw = 'Required';
    if (!newPw)                            errors.newPw = 'Required';
    else if (newPw.length < 8)             errors.newPw = 'Min. 8 characters';
    else if (!/[A-Z]/.test(newPw))         errors.newPw = 'Need 1 uppercase letter';
    else if (!/[0-9]/.test(newPw))         errors.newPw = 'Need 1 number';
    else if (!SPECIAL_RE.test(newPw))      errors.newPw = 'Need 1 special character';
    if (!confirmPw)                        errors.confirmPw = 'Required';
    else if (confirmPw !== newPw)          errors.confirmPw = 'Passwords do not match';
    if (Object.keys(errors).length) { setPwErrors(errors); return; }
    setPwErrors({});
    setPwSaving(true);
    try {
      await axios.post(
        `${API_BASE}/api/change-password`,
        { user_id: user.id, current_password: currentPw, new_password: newPw },
        { withCredentials: true },
      );
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setShowCurrent(false); setShowNew(false); setShowConfirm(false);
      pwFlash.flash('✓ Password changed successfully!', 'success');
    } catch (err) {
      setPwErrors({ currentPw: err.response?.data?.message || 'Current password is incorrect' });
    } finally {
      setPwSaving(false);
    }
  }, [currentPw, newPw, confirmPw, user, pwFlash]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div>
      <h1 style={{ fontFamily: 'Sora, sans-serif', fontSize: '22px', fontWeight: '600', color: '#22272D', marginBottom: '8px' }}>
        Settings
      </h1>
      <p style={{ fontSize: '13px', color: '#AAB0B8', marginBottom: '28px' }}>
        Manage your personal account details
      </p>

      {/* ── Name ── */}
      <div style={{ background: '#fff', border: '0.5px solid #AAB0B8', borderRadius: '12px', padding: '24px', marginBottom: '20px' }}>
        <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: '15px', fontWeight: '600', color: '#22272D', marginBottom: '20px', marginTop: 0 }}>
          Your name
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={labelStyle}>First name</label>
            <input
              autoComplete="off"
              value={firstName}
              onChange={e => { setFirstName(e.target.value); setNameErrors(p => ({ ...p, firstName: '' })); }}
              onKeyDown={e => e.key === 'Enter' && handleNameSave()}
              style={inputStyle(!!nameErrors.firstName)}
            />
            {nameErrors.firstName && <div style={errorStyle}>{nameErrors.firstName}</div>}
          </div>
          <div>
            <label style={labelStyle}>
              Last name <span style={{ color: '#AAB0B8', fontWeight: '400' }}>(optional)</span>
            </label>
            <input
              autoComplete="off"
              value={lastName}
              onChange={e => { setLastName(e.target.value); setNameErrors(p => ({ ...p, lastName: '' })); }}
              onKeyDown={e => e.key === 'Enter' && handleNameSave()}
              style={inputStyle(!!nameErrors.lastName)}
            />
            {nameErrors.lastName && <div style={errorStyle}>{nameErrors.lastName}</div>}
          </div>
        </div>
        {nameFlash.msg && <div style={msgBoxStyle(nameFlash.msgType)}>{nameFlash.msg}</div>}
        <button type="button" onClick={handleNameSave} disabled={nameSaving} style={saveBtn(nameSaving)}>
          {nameSaving ? 'Saving…' : 'Save Name'}
        </button>
      </div>

      {/* ── Email ── */}
      <div style={{ background: '#fff', border: '0.5px solid #AAB0B8', borderRadius: '12px', padding: '24px', marginBottom: '20px' }}>
        <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: '15px', fontWeight: '600', color: '#22272D', marginBottom: '20px', marginTop: 0 }}>
          Your email
        </h2>
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Email address</label>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={e => { setEmail(e.target.value); setEmailError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleEmailSave()}
            style={inputStyle(!!emailError)}
          />
          {emailError && <div style={errorStyle}>{emailError}</div>}
        </div>
        {emailFlash.msg && <div style={msgBoxStyle(emailFlash.msgType)}>{emailFlash.msg}</div>}
        <button type="button" onClick={handleEmailSave} disabled={emailSaving} style={saveBtn(emailSaving)}>
          {emailSaving ? 'Saving…' : 'Save Email'}
        </button>
      </div>

      {/* ── Password ── */}
      <div style={{ background: '#fff', border: '0.5px solid #AAB0B8', borderRadius: '12px', padding: '24px' }}>
        <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: '15px', fontWeight: '600', color: '#22272D', marginBottom: '20px', marginTop: 0 }}>
          Change password
        </h2>

        {/* Current password */}
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Current password</label>
          <div style={pwWrapStyle}>
            <input
              type={showCurrent ? 'text' : 'password'}
              autoComplete="current-password"
              value={currentPw}
              onChange={e => { setCurrentPw(e.target.value); setPwErrors(p => ({ ...p, currentPw: '' })); }}
              style={{ ...inputStyle(!!pwErrors.currentPw), paddingRight: '52px' }}
            />
            <ShowHideBtn show={showCurrent} onToggle={() => setShowCurrent(v => !v)} />
          </div>
          {pwErrors.currentPw && <div style={errorStyle}>{pwErrors.currentPw}</div>}
        </div>

        {/* New password */}
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>New password</label>
          <div style={pwWrapStyle}>
            <input
              type={showNew ? 'text' : 'password'}
              autoComplete="new-password"
              value={newPw}
              onChange={e => { setNewPw(e.target.value); setPwErrors(p => ({ ...p, newPw: '' })); }}
              style={{ ...inputStyle(!!pwErrors.newPw), paddingRight: '52px' }}
            />
            <ShowHideBtn show={showNew} onToggle={() => setShowNew(v => !v)} />
          </div>
          {pwErrors.newPw
            ? <div style={errorStyle}>{pwErrors.newPw}</div>
            : <div style={{ fontSize: '11px', color: '#AAB0B8', marginTop: '4px' }}>
                Min. 8 chars · 1 uppercase · 1 number · 1 special character
              </div>
          }
        </div>

        {/* Confirm new password */}
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Confirm new password</label>
          <div style={pwWrapStyle}>
            <input
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirmPw}
              onChange={e => { setConfirmPw(e.target.value); setPwErrors(p => ({ ...p, confirmPw: '' })); }}
              onKeyDown={e => e.key === 'Enter' && handlePasswordSave()}
              style={{ ...inputStyle(!!pwErrors.confirmPw), paddingRight: '52px' }}
            />
            <ShowHideBtn show={showConfirm} onToggle={() => setShowConfirm(v => !v)} />
          </div>
          {pwErrors.confirmPw && <div style={errorStyle}>{pwErrors.confirmPw}</div>}
          {/* Live match feedback */}
          {confirmPw && !pwErrors.confirmPw && (
            <div style={{ fontSize: '11px', marginTop: '4px', color: confirmPw === newPw ? '#1a6b1a' : '#FF766B' }}>
              {confirmPw === newPw ? '✓ Passwords match' : 'Passwords do not match'}
            </div>
          )}
        </div>

        {pwFlash.msg && <div style={msgBoxStyle(pwFlash.msgType)}>{pwFlash.msg}</div>}
        <button type="button" onClick={handlePasswordSave} disabled={pwSaving} style={saveBtn(pwSaving)}>
          {pwSaving ? 'Updating…' : 'Update Password'}
        </button>
      </div>
    </div>
  );
}

export default Settings;